/**
 * Session View — mirrors apps/experts/public/admin/session-view.html. Enter a
 * user (email or OID) + a window; GET /api/admin/session-view fans out to Sentry
 * / App Insights / qa_runs / chat and returns one chronological timeline plus a
 * per-source status (each can be PENDING independently). The query only fires
 * once a user has been submitted (enabled: Boolean(user)).
 */
import { useState } from 'react';
import {
  AdminEmpty,
  AdminError,
  AdminLoading,
  AdminPageHeader,
  Mono,
  Panel,
  StatusDot,
  Table,
  Td,
  Th,
  toneForStatus,
  Tr,
} from '../../components/primitives';
import { useSessionView } from '../../data-access/useAdmin';
import { absTime } from '../../lib/format';
import type { SessionViewSourceStatus } from '../../types';

const WINDOWS = [
  { minutes: 60, label: 'Last hour' },
  { minutes: 60 * 24, label: 'Last 24h' },
  { minutes: 60 * 24 * 7, label: 'Last 7 days' },
];

function severityTone(sev: string): 'ok' | 'warn' | 'error' | 'muted' {
  if (sev === 'error') return 'error';
  if (sev === 'warning') return 'warn';
  if (sev === 'info') return 'ok';
  return 'muted';
}

function SourceChip({
  name,
  src,
}: {
  name: string;
  src: SessionViewSourceStatus;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border border-border-subtle px-2 py-1 font-inter text-content-secondary text-xs">
      <StatusDot
        tone={toneForStatus(src.status === 'OK' ? 'ok' : src.status)}
      />
      {name}: {src.count}
      {src.error ? (
        <span className="text-amber-700" title={src.error}>
          !
        </span>
      ) : null}
    </span>
  );
}

export function SessionViewPage() {
  const [userInput, setUserInput] = useState('');
  const [user, setUser] = useState('');
  const [windowMinutes, setWindowMinutes] = useState(60);

  const { data, isPending, isError, error, isFetching } = useSessionView(
    user,
    windowMinutes,
  );
  const hasQuery = Boolean(user);

  return (
    <div>
      <AdminPageHeader
        subtitle="Unified per-user triage timeline across Sentry, App Insights, QA runs, and chat."
        title="Session View"
      />

      <Panel title="Lookup">
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            setUser(userInput.trim());
          }}
        >
          <label className="flex flex-col gap-1 font-inter text-content-secondary text-xs">
            User (email or OID)
            <input
              className="w-72 rounded-md border border-border-subtle bg-surface-background px-2 py-1.5 font-inter text-content-primary text-sm"
              onChange={(e) => setUserInput(e.target.value)}
              placeholder="advisor@cbtravel.com"
              type="text"
              value={userInput}
            />
          </label>
          <label className="flex flex-col gap-1 font-inter text-content-secondary text-xs">
            Window
            <select
              className="rounded-md border border-border-subtle bg-surface-background px-2 py-1.5 font-inter text-content-primary text-sm"
              onChange={(e) => setWindowMinutes(Number(e.target.value))}
              value={windowMinutes}
            >
              {WINDOWS.map((w) => (
                <option key={w.minutes} value={w.minutes}>
                  {w.label}
                </option>
              ))}
            </select>
          </label>
          <button
            className="rounded-md bg-surface-action-subtle px-3 py-1.5 font-inter text-content-action text-sm hover:opacity-90 disabled:opacity-50"
            disabled={!userInput.trim()}
            type="submit"
          >
            {isFetching ? 'Loading…' : 'View'}
          </button>
        </form>
      </Panel>

      {!hasQuery ? (
        <Panel className="mt-4">
          <AdminEmpty>Enter a user to load their session timeline.</AdminEmpty>
        </Panel>
      ) : null}

      {hasQuery && isPending ? (
        <Panel className="mt-4">
          <AdminLoading />
        </Panel>
      ) : null}

      {hasQuery && isError ? (
        <Panel className="mt-4">
          <AdminError
            message={`Failed to load: ${(error as Error)?.message ?? 'unknown'}`}
          />
        </Panel>
      ) : null}

      {data ? (
        <div className="mt-4 flex flex-col gap-4">
          <Panel title={`User: ${data.user.email ?? data.user.id}`}>
            <dl className="mb-3 grid grid-cols-2 gap-x-4 gap-y-1 font-inter text-sm sm:grid-cols-4">
              <div>
                <dt className="text-content-tertiary text-xs">Email</dt>
                <dd className="text-content-primary">
                  {data.user.email ?? '—'}
                </dd>
              </div>
              <div>
                <dt className="text-content-tertiary text-xs">Role</dt>
                <dd className="text-content-primary">
                  {data.user.role ?? '—'}
                </dd>
              </div>
              <div>
                <dt className="text-content-tertiary text-xs">OID</dt>
                <dd>
                  <Mono>{data.user.oid ?? '—'}</Mono>
                </dd>
              </div>
              <div>
                <dt className="text-content-tertiary text-xs">Window</dt>
                <dd className="text-content-primary">{data.window.minutes}m</dd>
              </div>
            </dl>
            <div className="flex flex-wrap gap-2">
              <SourceChip name="Sentry" src={data.sources.sentry} />
              <SourceChip name="App Insights" src={data.sources.app_insights} />
              <SourceChip name="QA runs" src={data.sources.qa_runs} />
              <SourceChip name="Chat" src={data.sources.chat} />
            </div>
          </Panel>

          <Panel title={`Timeline (${data.timeline.length})`}>
            {data.timeline.length === 0 ? (
              <AdminEmpty>No activity in this window.</AdminEmpty>
            ) : (
              <Table
                head={
                  <>
                    <Th>When</Th>
                    <Th>Source</Th>
                    <Th>Severity</Th>
                    <Th>Summary</Th>
                  </>
                }
              >
                {data.timeline.map((item, i) => (
                  <Tr key={`${item.ts}-${i}`}>
                    <Td className="whitespace-nowrap text-content-secondary text-xs">
                      {absTime(item.ts)}
                    </Td>
                    <Td>
                      <Mono>{item.source}</Mono>
                    </Td>
                    <Td>
                      <span className="inline-flex items-center gap-1.5">
                        <StatusDot tone={severityTone(item.severity)} />
                        {item.severity}
                      </span>
                    </Td>
                    <Td className="text-content-primary">{item.summary}</Td>
                  </Tr>
                ))}
              </Table>
            )}
          </Panel>
        </div>
      ) : null}
    </div>
  );
}

export default SessionViewPage;
